import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { BiEdit, BiShowAlt, BiHide } from "react-icons/bi";
import { AiOutlineCheck, AiOutlineClose, AiOutlineRight } from "react-icons/ai";
import { notifySuccess, notifyError } from "../../Services/Notifier";
import Util from "../../Classes/Util";
import { WidgetService } from "./Services/WidgetService";
import WidgetModal from "./WidgetModal";

const AllWidgets = () => {
    const permissions = Util.decrypt(localStorage.getItem("Permissions"));

    const [searchText, setSearchText] = useState("");
    const [pendingSearch, setPendingSearch] = useState("");
    const [widgets, setWidgets] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [showModal, setShowModal] = useState(false);
    const [activeId, setActiveId] = useState(null);

    const loadWidgets = async (
        p = page,
        s = pageSize,
        search = searchText
    ) => {
        try {
            const result = await WidgetService.getPaged(p, s, search);
            setWidgets(result.Data);
            setTotalRows(result.Total);
            setPage(p);
            setPageSize(s);
        } catch {
            notifyError("Failed to load widgets");
        }
    };


    const openAdd = () => {
        setActiveId(null);
        setShowModal(true);
    };

    const openEdit = (id) => {
        setActiveId(id);
        setShowModal(true);
    };

    const togglePublish = async (id) => {
        try {
            await WidgetService.togglePublish(id);
            notifySuccess("Status updated");
            loadWidgets();
        } catch {
            notifyError("Action failed");
        }
    };

    useEffect(() => {
        loadWidgets();
    }, []);

    const columns = useMemo(
        () => [
            { name: "ID", selector: r => r.WidgetId, sortable: true },
            { name: "Title", selector: r => r.Title, sortable: true },
            {
                name: "Published",
                cell: r => r.IsPublished ? <AiOutlineCheck /> : <AiOutlineClose />,
                center: true,
            },
            {
                name: "Actions",
                cell: r => (
                    <div className="d-flex gap-2">
                        {permissions.ManageWidget && (
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => openEdit(r.WidgetId)}
                            >
                                <BiEdit />
                            </button>
                        )}
                        {permissions.PublishWidget && (
                            <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => togglePublish(r.WidgetId)}
                            >
                                {r.IsPublished ? <BiHide /> : <BiShowAlt />}
                            </button>
                        )}
                    </div>
                ),
            },
        ],
        [permissions]
    );

    return (
        <div className="rightSide">
            <div className="container-fluid mt-3">
                <h2>Widgets</h2>
                <ul className="portalPath">
                    <li>Home <AiOutlineRight /></li>
                    <li>Widgets</li>
                </ul>

                <div className="card">
                    <div className="card-body">

                        <div className="row mb-3 align-items-center">
                            {/* SEARCH */}
                            <div className="col-md-8">
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search widgets..."
                                        value={pendingSearch}
                                        onChange={(e) => setPendingSearch(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                setSearchText(pendingSearch);
                                                loadWidgets(1, pageSize, pendingSearch);
                                            }
                                        }}
                                    />
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                            setSearchText(pendingSearch);
                                            loadWidgets(1, pageSize, pendingSearch);
                                        }}
                                    >
                                        Search
                                    </button>
                                </div>
                            </div>

                            {/* ADD BUTTON */}
                            <div className="col-md-4 text-end">
                                {permissions.ManageWidget && (
                                    <button className="btn btn-primary" onClick={openAdd}>
                                        + Add Widget
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="portalTable">
                            <DataTable
                                columns={columns}
                                data={widgets}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                paginationPerPage={pageSize}
                                onChangePage={p => loadWidgets(p, pageSize)}
                                onChangeRowsPerPage={(s, p) => loadWidgets(p, s)}
                                highlightOnHover
                                responsive
                            />
                        </div>
                    </div>
                </div>

                <WidgetModal
                    show={showModal}
                    widgetId={activeId}
                    onClose={() => setShowModal(false)}
                    onSaved={loadWidgets}
                />
            </div>
        </div>
    );
};

export default AllWidgets;
